// Aba de bosses: salas com teleporte e cooldown; o bosstiário mostra os abates de cada boss.
// Visual: covil / sala do trono sombria (vermelho-sangue, ferro, ossos e dourado nas recompensas) — CSS em client/css-parts/bosses.css (prefixo .bo-).
import { useEffect, useState } from 'preact/hooks';
import { BOSS_CAT_NAMES, BOSS_STAGE_NAMES, BOSS_TIERS, catOf, bossCooldownMs, bossLootBonus, bossPoints, bossSlotCount, bossStage, BOSS_ATTEMPTS, BOSS_BUY_PRICE, BOSS_LOOT_MASTERY, BOSS_LOOT_MAX, BOOSTED_BOSS_KILLS, type BossCat, type Bosstiary } from '../../../../shared/src/game/bosses';
import type { Outfit } from '../../../../shared/src/protocol';
import { Portrait } from './BattleList';
import { OutfitImage } from '../../screens/Characters';

interface Boss { name: string; level: number; cat?: BossCat; outfit: Outfit; hp: number; exp: number }
const n = (v: number) => v.toLocaleString('pt-BR');
const left = (ms: number) => { const m = Math.ceil(ms / 60000); return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m} min`; };
const cdText = (name: string) => { const h = Math.round(bossCooldownMs(name) / 3600_000); return h >= 48 ? `${Math.floor(h / 24)} dias${h % 24 ? ` e ${h % 24} h` : ''}` : `${h} h`; };
/** Boss Points para o 2º slot (regra de bossSlotCount) */
const SLOT2_PTS = 1500;
/** bônus mínimo / máximo pelos Boss Points (regra de bossLootBonus) */
const BONUS_MIN = 25, BONUS_PTS_MAX = BOSS_LOOT_MAX - BOSS_LOOT_MASTERY;
const MEDALS = ['🥉', '🥈', '🥇'];

function Medals(props: { stage: number; cat: BossCat }) {
  const t = BOSS_TIERS[props.cat];
  return (
    <span class="bo-medals">
      {[0, 1, 2].map((i) => (
        <i key={i} class={'bo-medal m' + (i + 1) + (props.stage > i ? ' on' : '')} title={`${BOSS_STAGE_NAMES[i + 1]}: ${n(t.kills[i]!)} abates (+${t.pts[i]} Boss Points)${props.stage > i ? ' — conquistado' : ''}`}>{MEDALS[i]}</i>
      ))}
    </span>
  );
}

export function Bosses(props: { list: Bosstiary; boosted: string | null; level: number; inCity: boolean; onEnter: (name: string, difficulty?: number) => void; slots: string[]; onSlot: (slot: number, name: string | null) => void;
  attempts?: { left: number; total: number; bought: number }; onBuy: () => void;
  /** enter = aba Bosses do Baiak (entrar); bosstiary = progresso do bosstiário; slots = Boss Slots e boss do dia */
  view?: 'enter' | 'bosstiary' | 'slots';
  /** Auto Boss: bosses salvos, se está rodando e a fila; onAuto manda set/start/stop */
  auto?: { list: string[]; on: boolean; queue: string[]; cur?: string }; onAuto?: (op: 'set' | 'start' | 'stop', list?: string[]) => void }) {
  const view = props.view ?? 'enter';
  const [bosses, setBosses] = useState<Boss[] | null>(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<'all' | BossCat>('all');
  const [mine, setMine] = useState(true);
  /** Phosphorus: dificuldade escolhida antes de teleportar (como no Global) */
  const [phosAsk, setPhosAsk] = useState<number | null>(null);
  const [help, setHelp] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { fetch('/api/bosses', { cache: 'no-cache' }).then((r) => r.json()).then(setBosses).catch(() => setBosses([])); }, []);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30_000); return () => clearInterval(t); }, []);
  const total = Object.values(props.list).reduce((t, b) => t + b.kills, 0);
  const pts = bossPoints(props.list), nSlots = bossSlotCount(props.list), bonus = bossLootBonus(pts);
  const stageOf = (b: Boss) => bossStage(props.list[b.name]?.kills ?? 0, catOf(b));
  const prowess = (bosses ?? []).filter((b) => stageOf(b) >= 1).map((b) => b.name);
  const byName = (name: string | null | undefined) => (name ? (bosses ?? []).find((b) => b.name === name) : undefined);
  const boostedBoss = byName(props.boosted);
  const counts = [1, 2, 3].map((s) => (bosses ?? []).filter((b) => stageOf(b) >= s).length);
  const now = Date.now();
  const readyCount = (bosses ?? []).filter((b) => props.level >= b.level && !(props.list[b.name] && props.list[b.name]!.next > now && props.boosted !== b.name)).length;
  const showHelp = help || total === 0;
  const bonusPct = Math.max(0, Math.min(100, ((bonus - BONUS_MIN) / (BONUS_PTS_MAX - BONUS_MIN)) * 100));

  const HEAD = {
    enter: { icon: '💀', title: 'Covil dos Bosses', sub: 'Salas do trono só suas. Os senhores de Noxara esperam.' },
    bosstiary: { icon: '📜', title: 'Bosstiário', sub: 'Cada abate é uma marca na sua lenda.' },
    slots: { icon: '👑', title: 'Boss Slots', sub: 'Dois pedestais de caça: quem estiver neles rende mais loot.' },
  }[view];

  const slotSelect = (i: number, big: boolean) => {
    if (i >= nSlots) {
      const why = i === 0 ? 'Libera com o primeiro Prowess' : `Libera com ${n(SLOT2_PTS)} Boss Points`;
      return (
        <div key={i} class={'bo-ped locked' + (big ? ' big' : '')} title={why}>
          <div class="bo-ped-top"><span class="bo-ped-lock">🔒</span></div>
          <div class="bo-ped-base">
            <b>Boss Slot {i + 1}</b>
            <small>{why}</small>
            {i === 1 && <span class="bo-bar"><i style={{ width: Math.min(100, (pts / SLOT2_PTS) * 100) + '%' }} /><em>{n(pts)} / {n(SLOT2_PTS)}</em></span>}
          </div>
        </div>
      );
    }
    const cur = byName(props.slots[i]);
    const mastery = cur ? stageOf(cur) >= 3 : false;
    return (
      <div key={i} class={'bo-ped' + (cur ? ' filled' : '') + (big ? ' big' : '')}>
        <div class="bo-ped-top">
          {cur ? (big ? <span class="bo-ped-img"><OutfitImage outfit={cur.outfit} /></span> : <span class="bo-ped-img"><Portrait outfit={cur.outfit} size={40} /></span>) : <span class="bo-ped-empty">?</span>}
        </div>
        <div class="bo-ped-base">
          <b>Boss Slot {i + 1}</b>
          <select value={props.slots[i] ?? ''} onChange={(e) => props.onSlot(i, (e.target as HTMLSelectElement).value || null)}>
            <option value="">Boss Slot {i + 1}: vazio</option>
            {prowess.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          {cur ? <small class="bo-ped-bonus">Loot <b>+{bonus + (mastery ? BOSS_LOOT_MASTERY : 0)}%</b>{mastery ? ' (com Mastery)' : ''}</small> : <small class="muted">Escolha um boss com Prowess</small>}
        </div>
      </div>
    );
  };

  return (
    <div class={'bo bo-' + view}>
      {/* cabeçalho: o portão do covil */}
      <div class="bo-head">
        <div class="bo-chains"><i /><i /></div>
        <div class="bo-emblem">{HEAD.icon}</div>
        <div class="bo-head-txt">
          <div class="bo-title">{HEAD.title} <button class="bo-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="bo-sub">{HEAD.sub}</div>
          <div class="bo-tags">
            {view === 'enter' && <><span>Sala só sua ou com a party</span><span>Cooldown de 20 h na maioria</span><span>Entre pela cidade</span></>}
            {view === 'bosstiary' && <><span>3 níveis por boss</span><span>Bane · Archfoe · Nemesis</span><span>Boss Points = mais loot</span></>}
            {view === 'slots' && <><span>Bônus de +{BONUS_MIN}% a +{BOSS_LOOT_MAX}%</span><span>+{BOSS_LOOT_MASTERY}% com Mastery</span><span>2º slot com {n(SLOT2_PTS)} pontos</span></>}
          </div>
        </div>
      </div>

      {/* números que importam */}
      <div class="bo-stats">
        {view === 'enter' && props.attempts && (
          <div class={'bo-stat' + (props.attempts.left ? '' : ' empty')} title="Sair sem bater no boss devolve a tentativa">
            <small>Tentativas hoje</small>
            <b>{props.attempts.left}<em>/{props.attempts.total}</em></b>
            <span class="bo-pips">{Array.from({ length: Math.min(props.attempts.total, 17) }, (_, i) => <i key={i} class={i < props.attempts!.left ? 'on' : ''} />)}</span>
          </div>
        )}
        {view === 'enter' && <div class="bo-stat" title="Bosses do seu level e fora do cooldown"><small>Prontos agora</small><b>{bosses ? n(readyCount) : '…'}</b><span>de {n(bosses?.length ?? 0)} bosses</span></div>}
        {view === 'bosstiary' && <div class="bo-stat"><small>Abates</small><b>{n(total)}</b><span>no bosstiário</span></div>}
        {view === 'bosstiary' && <div class="bo-stat medals"><small>Medalhas</small><b class="bo-medal-count"><span>🥉{counts[0]}</span><span>🥈{counts[1]}</span><span>🥇{counts[2]}</span></b><span>Prowess · Expertise · Mastery</span></div>}
        <div class="bo-stat gold" title="Somados de todos os níveis conquistados no bosstiário"><small>Boss Points</small><b>{n(pts)}</b><span>{nSlots}/2 slots liberados</span></div>
        <div class="bo-stat gold" title={`Bônus de loot dos bosses nos Boss Slots (+${BOSS_LOOT_MASTERY}% com Mastery; máximo +${BOSS_LOOT_MAX}%)`}>
          <small>Bônus de loot</small><b>+{bonus}<em>%</em></b>
          <span class="bo-bar thin"><i style={{ width: bonusPct + '%' }} /></span>
        </div>
        {view === 'slots' && <div class="bo-stat"><small>Abates</small><b>{n(total)}</b><span>no bosstiário</span></div>}
      </div>

      {showHelp && view === 'enter' && (
        <div class="bo-steps">
          <div><i>🏰</i><b>1. Na cidade</b><small>Escolha o boss e entre. Na party, o líder leva quem estiver na cidade.</small></div>
          <div><i>⚔️</i><b>2. A sala do trono</b><small>Uma sala só sua. Cada entrada gasta uma tentativa ({BOSS_ATTEMPTS.free} por dia, {BOSS_ATTEMPTS.vip} com VIP).</small></div>
          <div><i>⏳</i><b>3. Cooldown</b><small>Conta a partir da entrada: 20 h na maioria; Ferumbras 14 dias; Gaz'Haragoth, Orshabaal, Ghazbaran e Morgaroth 7 dias.</small></div>
          <div><i>★</i><b>4. Boss do dia</b><small>Sem cooldown, drop dobrado e conta {BOOSTED_BOSS_KILLS} abates no bosstiário.</small></div>
        </div>
      )}
      {showHelp && view === 'bosstiary' && (
        <div class="bo-steps">
          <div><i>🗡️</i><b>1. Abata</b><small>Cada boss morto soma um abate no bosstiário (o boss do dia soma {BOOSTED_BOSS_KILLS}).</small></div>
          <div><i>🥉</i><b>2. Prowess</b><small>Bane {BOSS_TIERS.bane.kills[0]}, Archfoe {BOSS_TIERS.archfoe.kills[0]}, Nemesis {BOSS_TIERS.nemesis.kills[0]} abate(s). Libera o boss para os Boss Slots.</small></div>
          <div><i>🥈</i><b>3. Expertise</b><small>Bane {BOSS_TIERS.bane.kills[1]}, Archfoe {BOSS_TIERS.archfoe.kills[1]}, Nemesis {BOSS_TIERS.nemesis.kills[1]}. Mais Boss Points.</small></div>
          <div><i>🥇</i><b>4. Mastery</b><small>Bane {BOSS_TIERS.bane.kills[2]}, Archfoe {BOSS_TIERS.archfoe.kills[2]}, Nemesis {BOSS_TIERS.nemesis.kills[2]}. +{BOSS_LOOT_MASTERY}% de loot no Boss Slot.</small></div>
        </div>
      )}
      {showHelp && view === 'slots' && (
        <div class="bo-steps three">
          <div><i>🥉</i><b>1. Consiga Prowess</b><small>Só bosses com Prowess no bosstiário podem ir para um pedestal. O 1º slot libera com o primeiro Prowess.</small></div>
          <div><i>👑</i><b>2. Coloque no slot</b><small>O boss no slot dá +{BONUS_MIN}% a +{BONUS_PTS_MAX}% de loot pelos seus Boss Points, +{BOSS_LOOT_MASTERY}% se tiver Mastery.</small></div>
          <div><i>📈</i><b>3. Suba os pontos</b><small>Cada nível do bosstiário dá Boss Points. Com {n(SLOT2_PTS)} pontos o 2º slot abre.</small></div>
        </div>
      )}

      {/* boss do dia */}
      {props.boosted && view !== 'bosstiary' && (
        <div class="bo-boosted">
          <span class="bo-boosted-img">{boostedBoss ? <OutfitImage outfit={boostedBoss.outfit} /> : <i>★</i>}</span>
          <div class="bo-boosted-txt">
            <small>★ BOSS DO DIA ★</small>
            <b>{props.boosted}</b>
            <span>Drop dobrado · sem cooldown · conta {BOOSTED_BOSS_KILLS} abates no bosstiário</span>
          </div>
          {view === 'enter' && boostedBoss && (() => {
            const locked = props.level < boostedBoss.level;
            return <button class="tb-button green" disabled={locked || !props.inCity} title={!props.inCity ? 'Volte para a cidade para entrar' : ''}
              onClick={() => (boostedBoss.name === 'Phosphorus' ? setPhosAsk(phosAsk ?? 1) : props.onEnter(boostedBoss.name))}>{locked ? `Level ${boostedBoss.level}` : 'Entrar'}</button>;
          })()}
        </div>
      )}

      {/* pedestais dos Boss Slots */}
      {view === 'slots' ? (
        <div class="bo-peds">{[0, 1].map((i) => slotSelect(i, true))}</div>
      ) : (
        <div class="bo-peds compact">{[0, 1].map((i) => slotSelect(i, false))}</div>
      )}

      {view === 'slots' && (
        <>
          <div class="bo-sec">Bosses prontos para o pedestal <small>· com Prowess no bosstiário ({prowess.length})</small></div>
          <div class="bo-cands">
            {!bosses && <p class="muted small">Carregando...</p>}
            {bosses && !prowess.length && <p class="bo-why">🔒 Nenhum boss com Prowess ainda. Abata bosses para liberar os Boss Slots.</p>}
            {(bosses ?? []).filter((b) => stageOf(b) >= 1).map((b) => {
              const stg = stageOf(b), inSlot = props.slots.includes(b.name);
              return (
                <div key={b.name} class={'bo-cand' + (inSlot ? ' on' : '') + (stg >= 3 ? ' mastery' : '')} title={inSlot ? 'Está num Boss Slot' : ''}>
                  <span class="bo-cand-img"><Portrait outfit={b.outfit} size={32} /></span>
                  <div><b>{b.name}</b><small>{BOSS_STAGE_NAMES[stg]} · +{bonus + (stg >= 3 ? BOSS_LOOT_MASTERY : 0)}%{inSlot ? ' · no slot' : ''}</small></div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view === 'enter' && props.auto && props.onAuto && (() => {
        const a = props.auto, max = props.attempts?.total ?? 10;
        return (
          <div class={'bo-auto' + (a.on ? ' on' : '')}>
            <div class="bo-auto-head">
              <b>🤖 Auto Boss</b>
              <span>{a.list.length}/{max} bosses salvos</span>
              {a.on
                ? <button class="tb-button bt-danger" onClick={() => props.onAuto!('stop')}>■ Parar</button>
                : <button class="tb-button green" disabled={!a.list.length} title={!a.list.length ? 'Marque os bosses com "+ Auto Boss" na lista' : 'Faz os bosses da lista um por um'} onClick={() => props.onAuto!('start')}>▶ Iniciar</button>}
            </div>
            <small class="muted">{a.on
              ? `Rodando${a.cur ? `: agora ${a.cur}` : ''}${a.queue.length ? ` · faltam ${a.queue.length} (${a.queue.join(', ')})` : ' · último da fila'}. Na arena a caça automática luta sozinha; quando o boss morre, vai para o próximo.`
              : `Marque até ${max} bosses (suas tentativas do dia) com "+ Auto Boss" e clique em Iniciar. Quem estiver em espera, sem level ou sem tentativa é pulado. Funciona com você no jogo (não na caça offline).`}</small>
            {!!a.list.length && (
              <div class="bo-auto-list">
                {a.list.map((nm, i) => (
                  <span key={nm} class={'bo-auto-chip' + (a.on && a.cur === nm ? ' cur' : a.on && !a.queue.includes(nm) ? ' done' : '')}>
                    {i + 1}. {nm}
                    {!a.on && <button title="Tirar do Auto Boss" onClick={() => props.onAuto!('set', a.list.filter((x) => x !== nm))}>✕</button>}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {view === 'enter' && props.attempts && (
        <div class="bo-attempts">
          <span>Tentativas hoje: <b class={props.attempts.left ? 'bo-gold' : 'error'}>{props.attempts.left}</b> de {props.attempts.total}</span>
          <small class="muted">({BOSS_ATTEMPTS.free} por dia, {BOSS_ATTEMPTS.vip} com VIP; sair sem bater no boss devolve a tentativa)</small>
          <button class="tb-button" disabled={props.attempts.bought >= 1} title={props.attempts.bought >= 1 ? 'Você já comprou tentativas hoje' : ''} onClick={props.onBuy}>+{BOSS_ATTEMPTS.buy} tentativas ({BOSS_BUY_PRICE} coins)</button>
        </div>
      )}

      {view !== 'slots' && (
        <div class="bo-filter">
          <input class="mk-search" placeholder="Buscar boss" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          {(['all', 'bane', 'archfoe', 'nemesis'] as const).map((c) => <button key={c} class={'tb-button' + (cat === c ? ' green' : '')} onClick={() => setCat(c)}>{c === 'all' ? 'Todos' : BOSS_CAT_NAMES[c]}</button>)}
          <label class="small"><input type="checkbox" checked={mine} onChange={(e) => setMine((e.target as HTMLInputElement).checked)} /> Até o meu level</label>
          <small class="muted">{bosses?.length ?? 0} bosses</small>
        </div>
      )}

      {phosAsk !== null && (
        <div class="bo-phos">
          <b>🌙 Phosphorus — escolha a dificuldade</b>
          <div class="bo-phos-row">
            <button class="tb-button" onClick={() => setPhosAsk(Math.max(1, phosAsk - 1))}>−</button>
            <input type="range" min={1} max={30} value={phosAsk} onInput={(e) => setPhosAsk(Number((e.target as HTMLInputElement).value))} />
            <button class="tb-button" onClick={() => setPhosAsk(Math.min(30, phosAsk + 1))}>+</button>
            <b class="bo-phos-lvl">{phosAsk}</b>
          </div>
          <small>Monstros: +{phosAsk * 10}% de dano e +{phosAsk * 5}% de vida · loot +{phosAsk * 2}%.</small>
          <small class={phosAsk >= 5 ? 'on' : 'off'}>5+: Scraps of a Radiant Attire no loot.</small>
          <small class={phosAsk >= 10 ? 'on' : 'off'}>10+: novos Moonsilver Protectors na vulnerabilidade, Augur com cada Rival, Sentinels (campos de tempestade), Campos Drenantes na fase final, Cloud in a Bottle no loot.</small>
          <small class={phosAsk >= 15 ? 'on' : 'off'}>15+: as magias de área do Phosphorus pegam a arena inteira e há mais Campos Drenantes.</small>
          <small class={phosAsk >= 25 ? 'on' : 'off'}>25+: Auric Moon Sigil no loot ({(1.25 + 0.05 * Math.max(0, phosAsk - 25)).toFixed(2).replace('.', ',')}%).</small>
          <div class="bo-phos-row">
            <button class="tb-button green" onClick={() => { props.onEnter('Phosphorus', phosAsk); setPhosAsk(null); }}>Teleportar</button>
            <button class="tb-button" onClick={() => setPhosAsk(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {view !== 'slots' && (
        <div class={'bo-list ' + (view === 'bosstiary' ? 'grid' : 'rows')}>
          {!bosses && <p class="muted">Carregando...</p>}
          {bosses?.filter((b) => (!q || b.name.toLowerCase().includes(q.toLowerCase())) && (cat === 'all' || catOf(b) === cat) && (!mine || b.level <= props.level || props.list[b.name])).map((b) => {
            const st = props.list[b.name];
            const wait = st && st.next > now && props.boosted !== b.name ? st.next - now : 0;
            const locked = props.level < b.level;
            const c = catOf(b), k = st?.kills ?? 0, stg = bossStage(k, c), tiers = BOSS_TIERS[c].kills;
            const prev = stg === 0 ? 0 : tiers[stg - 1]!, next = tiers[Math.min(2, stg)]!;
            const prog = stg >= 3 ? 100 : Math.max(0, Math.min(100, ((k - prev) / (next - prev)) * 100));
            const boosted = props.boosted === b.name, inSlot = props.slots.includes(b.name);
            const state = locked ? 'locked' : wait ? 'cd' : 'ready';
            const cdPct = wait ? Math.max(0, Math.min(100, (1 - wait / bossCooldownMs(b.name)) * 100)) : 0;
            return (
              <div key={b.name} class={'bo-card ' + state + (boosted ? ' boosted' : '') + (stg >= 3 ? ' mastery' : '') + ' c-' + c}>
                <span class="bo-face"><Portrait outfit={b.outfit} size={48} />{boosted && <em class="bo-face-star">★</em>}</span>
                <div class="bo-card-txt">
                  <div class="bo-card-name"><b>{b.name}</b><span class={'bo-cat c-' + c}>{BOSS_CAT_NAMES[c]}</span>{inSlot && <span class="bo-chip slot" title="Está num Boss Slot">👑 slot</span>}</div>
                  {view === 'enter' && <small>Level {b.level}+ · {n(b.hp)} de vida · {n(b.exp)} XP · volta em {cdText(b.name)}</small>}
                  {view === 'bosstiary' && <small>Level {b.level}+ · {n(b.hp)} de vida</small>}
                  <div class="bo-prog">
                    <Medals stage={stg} cat={c} />
                    <span class="bo-bar"><i style={{ width: prog + '%' }} /><em>{BOSS_STAGE_NAMES[stg]} · {n(k)}{stg < 3 ? ` / ${n(next)}` : ''} abates</em></span>
                  </div>
                </div>
                {view === 'enter' && (
                  <div class="bo-act">
                    {wait ? <span class="bo-cd" title="Cooldown conta a partir da entrada"><span class="bo-bar thin cd"><i style={{ width: cdPct + '%' }} /></span><small>⏳ volta em <b>{left(wait)}</b></small></span>
                      : locked ? <small class="bo-why">🔒 precisa do level {b.level}</small>
                      : !props.inCity ? <small class="bo-why">Volte para a cidade</small>
                      : <small class="bo-ok">● disponível</small>}
                    <button class="tb-button green" disabled={locked || !!wait || !props.inCity} title={!props.inCity ? 'Volte para a cidade para entrar' : ''}
                      onClick={() => (b.name === 'Phosphorus' ? setPhosAsk(phosAsk ?? 1) : props.onEnter(b.name))}>{locked ? `Level ${b.level}` : wait ? `Volta em ${left(wait)}` : 'Entrar'}</button>
                    {props.auto && props.onAuto && b.name !== 'Phosphorus' && !locked && (() => {
                      const inAuto = props.auto!.list.includes(b.name), full = props.auto!.list.length >= (props.attempts?.total ?? 10);
                      return <button class={'tb-button bo-auto-add' + (inAuto ? ' on' : '')} disabled={props.auto!.on || (!inAuto && full)}
                        title={props.auto!.on ? 'Pare o Auto Boss para mudar a lista' : inAuto ? 'Tirar do Auto Boss' : full ? 'A lista já tem o máximo (suas tentativas do dia)' : 'Colocar na fila do Auto Boss'}
                        onClick={() => props.onAuto!('set', inAuto ? props.auto!.list.filter((x) => x !== b.name) : [...props.auto!.list, b.name])}>{inAuto ? '✓ Auto Boss' : '+ Auto Boss'}</button>;
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
